<script lang="ts">
  import type { ToolCursor } from '$lib/core/interaction';
  import SnapBar from './SnapBar.svelte';
  import { vec, type Vec2 } from '$lib/core/geom';
  import { pansView, wheelGesture } from '$lib/core/interaction';
  import { layoutsOf } from '$lib/core/scene';
  import { paletteFor, render } from '$lib/render';
  import type { EditorState } from '../editor-state.svelte';

  const { editor }: { editor: EditorState } = $props();

  let canvas = $state<HTMLCanvasElement | null>(null);
  let host = $state<HTMLDivElement | null>(null);
  let dark = $state(false);
  let panning = $state(false);
  let last = vec(0, 0);
  let frame = 0;

  const palette = $derived(paletteFor(dark));
  const layouts = $derived(layoutsOf(editor.scene));

  function pointOf(event: MouseEvent): Vec2 {
    const rect = canvas?.getBoundingClientRect();
    if (!rect) return vec(0, 0);
    return vec(event.clientX - rect.left, event.clientY - rect.top);
  }

  function onPointerDown(event: PointerEvent) {
    // A focused panel field would otherwise keep swallowing keyboard shortcuts.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    canvas?.setPointerCapture(event.pointerId);

    if (pansView(event.button, event.altKey, editor.panHeld)) {
      panning = true;
      last = pointOf(event);
      return;
    }

    editor.pointerDown(pointOf(event), event.button, event.shiftKey);
  }

  function onPointerMove(event: PointerEvent) {
    if (panning) {
      const now = pointOf(event);
      editor.panBy(now.x - last.x, now.y - last.y);
      last = now;
      return;
    }

    editor.pointerMove(pointOf(event), event.button, event.shiftKey);
  }

  function onPointerUp(event: PointerEvent) {
    canvas?.releasePointerCapture(event.pointerId);

    if (panning) {
      panning = false;
      return;
    }

    editor.pointerUp(pointOf(event), event.button, event.shiftKey);
  }

  function onPointerLeave() {
    editor.pointerLeave();
  }

  function onWheel(event: WheelEvent) {
    event.preventDefault();
    const gesture = wheelGesture({
      deltaX: event.deltaX,
      deltaY: event.deltaY,
      deltaMode: event.deltaMode,
      zoom: event.ctrlKey || event.metaKey,
    });
    if (gesture.kind === 'pan') editor.panBy(gesture.dx, gesture.dy);
    else editor.zoomAtScreen(pointOf(event), gesture.factor);
  }

  function onDoubleClick(event: MouseEvent) {
    editor.doubleClick(pointOf(event));
  }

  function onContextMenu(event: MouseEvent) {
    event.preventDefault();
    editor.contextMenu();
  }

  function draw() {
    const element = canvas;
    const ctx = element?.getContext('2d');
    if (!element || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = editor.viewport.width;
    const height = editor.viewport.height;

    if (element.width !== Math.round(width * dpr) || element.height !== Math.round(height * dpr)) {
      element.width = Math.round(width * dpr);
      element.height = Math.round(height * dpr);
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    render(ctx, {
      viewport: editor.viewport,
      palette,
      entities: editor.scene.entities,
      layouts,
      selection: new Set(editor.selection),
      hoveredId: editor.hoveredId,
      highlighted: editor.highlighted,
      previews: editor.previews,
      regions: editor.shownRegion ? [editor.shownRegion] : [],
      snap: editor.activeSnap,
      marquee: editor.marquee,
      showGrid: editor.showGrid,
      gridSize: editor.snapSettings.gridSize,
    });
  }

  $effect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    dark = query.matches;
    const onChange = (event: MediaQueryListEvent) => (dark = event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  });

  $effect(() => {
    const element = host;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      editor.resizeViewport(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  $effect(() => {
    // Touch every input the frame depends on, then coalesce into one paint.
    void [
      editor.scene,
      editor.viewport,
      editor.selection,
      editor.hoveredId,
      editor.highlighted,
      editor.previews,
      editor.shownRegion,
      editor.activeSnap,
      editor.marquee,
      editor.showGrid,
      editor.snapSettings.gridSize,
      palette,
    ];
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  });

  /**
   * The pointer for each tool's cursor style. Measure is a crosshair with a small ruler below
   * and right of it; the hotspot is the cross's center, and the white outline keeps it visible
   * on dark and light canvases alike.
   */
  const MEASURE_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><g stroke-linecap='square'><path d='M10 1v18M1 10h18' stroke='white' stroke-width='3'/><path d='M10 1v18M1 10h18' stroke='black' stroke-width='1'/><g transform='rotate(-35 20 23)'><rect x='11.5' y='19.5' width='17' height='7' fill='white' stroke='black' stroke-width='1'/><path d='M14.5 19.5v3M17.5 19.5v2M20.5 19.5v3M23.5 19.5v2M26.5 19.5v3' stroke='black' stroke-width='1'/></g></g></svg>`;
  const CURSORS: Record<ToolCursor, string> = {
    crosshair: 'crosshair',
    measure: `url("data:image/svg+xml,${encodeURIComponent(MEASURE_SVG)}") 10 10, crosshair`,
  };
</script>

<div class="stage" bind:this={host}>
  <canvas
    bind:this={canvas}
    style:cursor={panning ? 'grabbing' : editor.panHeld ? 'grab' : CURSORS[editor.toolCursor]}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointerleave={onPointerLeave}
    onwheel={onWheel}
    ondblclick={onDoubleClick}
    oncontextmenu={onContextMenu}
  ></canvas>
  <SnapBar {editor} />
</div>

<style>
  .stage {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: var(--color-bg);
  }

  canvas {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: crosshair;
  }
</style>
